import StatusSwitch from 'components/StatusSwitch';
import { updatePauseTask } from '../service';
import { Popover, message } from 'antd';
import { useTranslate } from '@tolgee/react';
import ReceptionMinuteSelect from 'components/ReceptionMinuteSelect';
import { useState } from 'react';
import styled from '@emotion/styled';
import RobotIcon from 'components/RobotIcon';
import { Timer } from 'views/Conversation/components/AiReceptionForm/AiReceptionTimer';

export interface PauseModeProps {
  readonly?: boolean;
  value: Aide.Pause.Task;
  onChange: (value: Partial<Aide.Pause.Task>) => void;
}

function PauseMode(props: PauseModeProps) {
  const { readonly = false, value, onChange } = props;

  const { humanFirst, pauseAi, pauseMinute } = value;

  const { t } = useTranslate();

  const [minuteLoading, setMinuteLoading] = useState(false);

  return (
    <Container>
      <header>
        <h3>{t('auto_reply_pause_mode')}</h3>
        <Popover
          placement="bottomLeft"
          content={
            <Example>
              {t('pause_auto_reply_example_title')}
              <div>
                <dl>
                  <dt>
                    <RobotIcon color="var(--fett-text-tertiary)" status="working" />
                  </dt>
                  <dd>{t('pause_auto_reply_example_1')}</dd>
                </dl>
                <dl>
                  <dt>
                    <RobotIcon color="var(--fett-text-tertiary)" status="paused" />
                  </dt>
                  <dd>{t('pause_auto_reply_example_2')}</dd>
                </dl>
                <dl>
                  <dt>
                    <RobotIcon color="var(--fett-text-tertiary)" status="paused" />
                    <Timer>18:28:58</Timer>
                  </dt>
                  <dd>{t('pause_auto_reply_example3')}</dd>
                </dl>
              </div>
            </Example>
          }
        >
          <span>{t('marketing.示例')}</span>
        </Popover>
      </header>
      <Content>
        <dl>
          <dt>
            {t('人工客服优先智能客服自动回复')}
            <StatusSwitch
              disabled={readonly}
              checkedChildren={t('marketing.启用中')}
              unCheckedChildren={t('marketing.未启用')}
              checked={humanFirst}
              onChange={(humanFirst) =>
                updatePauseTask({
                  ...value,
                  humanFirst,
                })
                  .then(() => {
                    onChange({ humanFirst });

                    message.success(t('operation.saveSucceed'));
                  })
                  .catch(() => {
                    message.error(t('operation.saveFailed'));
                  })
              }
            />
          </dt>
          <dd>{t('人工客服优先智能客服自动回复tips')}</dd>
        </dl>
        <dl>
          <dt>
            {t('人工客服手动暂停智能客服自动回复')}
            <StatusSwitch
              disabled={readonly}
              checkedChildren={t('marketing.启用中')}
              unCheckedChildren={t('marketing.未启用')}
              checked={pauseAi}
              onChange={(pauseAi) =>
                updatePauseTask({
                  ...value,
                  pauseAi,
                })
                  .then(() => {
                    onChange({ pauseAi });

                    message.success(t('operation.saveSucceed'));
                  })
                  .catch(() => {
                    message.error(t('operation.saveFailed'));
                  })
              }
            />
          </dt>
          <dd>
            {t('人工客服手动暂停智能客服自动回复tips')}
            <div>
              {t('default_pause_time')}
              <ReceptionMinuteSelect
                dropdownMatchSelectWidth={false}
                value={pauseMinute}
                loading={minuteLoading}
                disabled={minuteLoading || readonly}
                onChange={(pauseMinute) => {
                  setMinuteLoading(true);

                  onChange({ pauseMinute });

                  updatePauseTask({
                    ...value,
                    pauseMinute,
                  })
                    .then(() => {
                      message.success(t('operation.saveSucceed'));
                    })
                    .catch(() => {
                      message.error(t('operation.saveFailed'));

                      onChange({ pauseMinute: value.pauseMinute });
                    })
                    .finally(() => {
                      setMinuteLoading(false);
                    });
                }}
              />
            </div>
          </dd>
        </dl>
      </Content>
    </Container>
  );
}

export const Container = styled.div`
  margin-bottom: 16px;
  > * {
    background: var(--fett-bg-panel);
  }
  > header {
    display: flex;
    align-items: center;
    height: 56px;
    padding: 0 24px;
    margin-bottom: 1px;
    > h3 {
      margin-bottom: 0;
    }
    > span {
      margin-left: 12px;
      cursor: pointer;
      &:hover {
        color: var(--ant-primary-color);
      }
    }
  }
  > div {
    padding: 16px;
  }
  &:last-child {
    margin-bottom: 0;
  }
`;

const Content = styled.div`
  > dl {
    padding: 14px 16px;
    border: solid 1px var(--fett-border-default);
    > dt {
      display: flex;
      align-items: center;
      margin-bottom: 12px;
      > * {
        margin-left: 8px;
      }
    }
    > dd {
      margin-bottom: 0;
      > div {
        display: flex;
        align-items: center;
        height: 48px;
        padding: 0 12px;
        margin-top: 8px;
        background: var(--fett-erp-card-bg);
      }
    }
  }
`;

const Example = styled.div`
  > div {
    padding: 12px;
    margin-top: 6px;
    font-size: 12px;
    background: var(--fett-erp-card-bg);
    > dl {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      > dt {
        display: flex;
        align-items: center;
        height: 22px;
        padding: 0 3px;
        font-size: 14px;
        background: var(--fett-bg-panel);
        border: solid 1px var(--fett-border-strong);
        > span {
          margin-left: 0;
          font-weight: 300;
          background: var(--fett-bg-panel);
        }
      }
      > dd {
        margin-bottom: 0;
        margin-left: 6px;
      }
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`;

export default PauseMode;
